import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import {
  Client,
  FirmwareUpdateByLocationDataIn,
  GuidDictionaryItem,
  MeterModelDetailsDataOut,
} from '../../../../api/api-reference';
import { toUtcDate } from '../../../../helper/date-utils';
import { DatePickerComponent } from '../../../core/components/date-picker/date-picker.component';
import { TimePickerComponent } from '../../../core/components/time-picker/time-picker.component';

@Component({
  selector: 'app-create-region-based-firmware-update',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    AngularSvgIconModule,
    RouterLink,
    DatePickerComponent,
    TimePickerComponent,
  ],
  templateUrl: './create-region-based-firmware-update.component.html',
  styleUrl: './create-region-based-firmware-update.component.scss',
  providers: [Client],
})
export class CreateRegionBasedFirmwareUpdateComponent implements OnInit {
  regions: GuidDictionaryItem[] = [];
  areas: GuidDictionaryItem[] = [];
  meterMakes: GuidDictionaryItem[] = [];
  meterModels: MeterModelDetailsDataOut[] = [];
  targetMeterModels: MeterModelDetailsDataOut[] = [];

  selectedRegions: GuidDictionaryItem[] = [];
  selectedAreas: GuidDictionaryItem[] = [];
  submitted = false;
  showSchedulePanel = false;

  firmwareUpdateForm = this.fb.group({
    regions: [[] as string[], Validators.required],
    areas: [[] as string[]],
    meterMakeId: ['', Validators.required],
    meterModelId: [{ value: '', disabled: true }, Validators.required],
    targetMeterModelId: [{ value: '', disabled: true }, Validators.required],
    maxRetries: [1, [Validators.required, Validators.min(1), Validators.max(10)]],
    scheduledDate: [''],
    scheduledTime: [''],
  });

  constructor(
    private fb: FormBuilder,
    private client: Client,
    private toastr: ToastrService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.getRegions();
    this.getMeterMakes();
  }

  get canUpdate(): boolean {
    const formValue = this.firmwareUpdateForm.getRawValue();

    return this.firmwareUpdateForm.valid;
  }

  get canSchedule(): boolean {
    return this.canUpdate && !!this.scheduledDateControl.value && !!this.scheduledTimeControl.value;
  }

  get filteredTargetMeterModels(): MeterModelDetailsDataOut[] {
    return this.targetMeterModels.filter((item) => item.id !== this.meterModelIdControl.value);
  }

  get availableRegions(): GuidDictionaryItem[] {
    return this.regions.filter((item) => !this.isSelectedRegion(item));
  }

  get availableAreas(): GuidDictionaryItem[] {
    return this.areas.filter((item) => !this.isSelectedArea(item));
  }

  get regionsControl(): AbstractControl {
    return this.firmwareUpdateForm.get('regions')!;
  }

  get areasControl(): AbstractControl {
    return this.firmwareUpdateForm.get('areas')!;
  }

  get meterMakeIdControl(): AbstractControl {
    return this.firmwareUpdateForm.get('meterMakeId')!;
  }

  get meterModelIdControl(): AbstractControl {
    return this.firmwareUpdateForm.get('meterModelId')!;
  }

  get targetMeterModelIdControl(): AbstractControl {
    return this.firmwareUpdateForm.get('targetMeterModelId')!;
  }

  get maxRetriesControl(): AbstractControl {
    return this.firmwareUpdateForm.get('maxRetries')!;
  }

  get scheduledDateControl(): AbstractControl {
    return this.firmwareUpdateForm.get('scheduledDate')!;
  }

  get scheduledTimeControl(): AbstractControl {
    return this.firmwareUpdateForm.get('scheduledTime')!;
  }

  getRegions(): void {
    this.client.getAllRegionsForOptions().subscribe({
      next: (response) => {
        this.regions = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  onRegionChange(event: Event): void {
    const regionId = (event.target as HTMLSelectElement).value;

    if (!regionId) {
      return;
    }

    const selectedValue = this.regions.find((item) => item.value === regionId);

    if (selectedValue && !this.isSelectedRegion(selectedValue)) {
      this.selectedRegions.push(selectedValue);
      this.syncSelectedRegions();
      this.loadAreasForSelectedRegions();
    }

    (event.target as HTMLSelectElement).value = '';
  }

  removeRegion(selectedValue: GuidDictionaryItem): void {
    if (this.isSelectedRegion(selectedValue)) {
      this.selectedRegions = this.selectedRegions.filter(
        (item) => item.value !== selectedValue.value,
      );
      this.syncSelectedRegions();
      this.loadAreasForSelectedRegions();
    }
  }

  clearSelectedRegions(): void {
    this.selectedRegions = [];
    this.syncSelectedRegions();
    this.areas = [];
    this.clearSelectedAreas();
  }

  isSelectedRegion(selectedValue: GuidDictionaryItem): boolean {
    return this.selectedRegions.some((item) => item.value === selectedValue.value);
  }

  onAreaChange(event: Event): void {
    const areaId = (event.target as HTMLSelectElement).value;

    if (!areaId) {
      return;
    }

    const selectedValue = this.areas.find((item) => item.value === areaId);
    if (selectedValue && !this.isSelectedArea(selectedValue)) {
      this.selectedAreas.push(selectedValue);
      this.syncSelectedAreas();
    }

    (event.target as HTMLSelectElement).value = '';
  }

  removeArea(selectedValue: GuidDictionaryItem): void {
    if (this.isSelectedArea(selectedValue)) {
      this.selectedAreas = this.selectedAreas.filter(
        (item) => item.value !== selectedValue.value,
      );
      this.syncSelectedAreas();
    }
  }

  clearSelectedAreas(): void {
    this.selectedAreas = [];
    this.syncSelectedAreas();
  }

  isSelectedArea(selectedValue: GuidDictionaryItem): boolean {
    return this.selectedAreas.some((item) => item.value === selectedValue.value);
  }

  isSelectedMeterMake(): boolean {
    return !!this.meterMakeIdControl.value;
  }

  isSelectedMeterModel(): boolean {
    return !!this.meterModelIdControl.value;
  }

  private loadAreasForSelectedRegions(): void {
    const regionIds = this.selectedRegions
      .map((item) => item.value)
      .filter((value): value is string => !!value);

    if (regionIds.length === 0) {
      this.areas = [];
      this.clearSelectedAreas();
      return;
    }

    this.client.getAllAreaByRegionsForOptions(regionIds).subscribe({
      next: (response) => {
        this.areas = response ?? [];
        const availableAreaIds = new Set(
          this.areas
            .map((item) => item.value)
            .filter((value): value is string => !!value),
        );

        this.selectedAreas = this.selectedAreas.filter(
          (item) => !!item.value && availableAreaIds.has(item.value),
        );
        this.syncSelectedAreas();
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getMeterMakes(): void {
    this.client.getAllMeterMakersForSetupOptions().subscribe({
      next: (response) => {
        this.meterMakes = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getMeterModelLabel(meterModel: MeterModelDetailsDataOut): string {
    return [
      meterModel.model,
      meterModel.firmwareVersion ? `(${meterModel.firmwareVersion})` : undefined,
    ].filter(Boolean).join(' ');
  }

  onMeterMakeChange(): void {
    this.meterModelIdControl.setValue('');
    this.targetMeterModelIdControl.setValue('');
    this.meterModelIdControl.disable();
    this.targetMeterModelIdControl.disable();
    this.meterModels = [];
    this.targetMeterModels = [];

    const selectedMeterMakeId = this.meterMakeIdControl.value as string;
    if (!selectedMeterMakeId) {
      return;
    }

    this.client.getAllMeterModelForMeterMakes2([selectedMeterMakeId]).subscribe({
      next: (response) => {
        this.meterModels = response ?? [];
        this.targetMeterModels = response ?? [];

        if (this.meterModels.length > 0) {
          this.meterModelIdControl.enable();
        }
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  onMeterModelChange(): void {
    this.targetMeterModelIdControl.setValue('');

    if (this.meterModelIdControl.value && this.filteredTargetMeterModels.length > 0) {
      this.targetMeterModelIdControl.enable();
    } else {
      this.targetMeterModelIdControl.disable();
    }
  }

  increment(): void {
    const value = Number(this.maxRetriesControl.value ?? 0);
    if (value < 10) {
      this.maxRetriesControl.setValue(value + 1);
    }
  }

  decrement(): void {
    const value = Number(this.maxRetriesControl.value ?? 0);
    if (value > 1) {
      this.maxRetriesControl.setValue(value - 1);
    }
  }

  clearRetries(): void {
    this.maxRetriesControl.setValue(0);
  }

  updateNow(): void {
    this.sendFirmwareUpdate();
  }

  scheduleUpdate(): void {
    this.sendFirmwareUpdate(this.parseScheduledDateTime());
  }

  private parseScheduledDateTime(): Date {
    const timeStr = (this.scheduledTimeControl.value as string).toLowerCase();
    const isPm = timeStr.includes('pm');
    const [hourStr, minuteStr] = timeStr.replace(/am|pm/, '').split(':');
    let hours = parseInt(hourStr, 10);
    const minutes = parseInt(minuteStr, 10);

    if (isPm && hours !== 12) {
      hours += 12;
    }

    if (!isPm && hours === 12) {
      hours = 0;
    }

    const [year, month, day] = (this.scheduledDateControl.value as string).split('-').map(Number);
    return toUtcDate(new Date(year, month - 1, day, hours, minutes, 0));
  }

  private sendFirmwareUpdate(deferredTaskTime?: Date): void {
    this.submitted = true;

    if (!this.canUpdate || (this.showSchedulePanel && !this.canSchedule)) {
      this.firmwareUpdateForm.markAllAsTouched();
      return;
    }

    const formValue = this.firmwareUpdateForm.getRawValue();
    const dataIn = new FirmwareUpdateByLocationDataIn();
    dataIn.regions = formValue.regions ?? [];
    dataIn.areas = formValue.areas ?? [];
    dataIn.meterModelId = formValue.meterModelId!;
    dataIn.targetMeterModelId = formValue.targetMeterModelId!;
    dataIn.maxRetryCount = formValue.maxRetries!;
    dataIn.deferredTaskTime = deferredTaskTime;

    this.client.updateFirmwareImageByLocation(dataIn).subscribe({
      next: () => {
        this.toastr.success('Firmware update scheduled successfully.');
        this.router.navigate(['/firmware-update-group']);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  setScheduledDate(value: string): void {
    this.scheduledDateControl.setValue(value);
    this.scheduledDateControl.markAsDirty();
  }

  setScheduledTime(value: string): void {
    this.scheduledTimeControl.setValue(value);
    this.scheduledTimeControl.markAsDirty();
  }

  private syncSelectedRegions(): void {
    this.regionsControl.setValue(this.selectedRegions.map((item) => item.value!));
    this.regionsControl.markAsDirty();
  }

  private syncSelectedAreas(): void {
    this.areasControl.setValue(this.selectedAreas.map((item) => item.value!));
    this.areasControl.markAsDirty();
  }
}
